import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import type { Company } from '../../../@types/Company';
import type { FixedNewsletter } from '../../../@types/Newsletter';
import { updateCompanyBranding } from '../../../store/AuthReducer';
import type { RootState } from '../../../store/store';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import { showToast } from '../../common/Notification';
import PreviewViewer from '../../common/PreviewViewer/PreviewViewer.module';

interface NewsletterTemplateSelectionProps {
  hideBackButton?: boolean;
  onSelected?: (templateName: string) => void;
}

function NewsletterTemplateSelection({ hideBackButton = false, onSelected }: NewsletterTemplateSelectionProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const handleBack = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST);
    }
  };
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company) as Company;

  const [newsletters, setNewsletters] = useState<FixedNewsletter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [shouldScrollToSelected, setShouldScrollToSelected] = useState(false);

  useEffect(() => {
    const fetchNewslettersAndTemplates = async() => {
      if (!jwt || !company?.id) {
        setIsLoading(false);
        return;
      }

      try {
        // Fetch fixed newsletters to get available templates
        const response = await fetch(`/api/companies/${company.id}/fixed-newsletters`, JWTHeader(jwt));
        if (!response.ok) {
          throw new Error('Failed to fetch newsletters');
        }
        const data = await response.json();
        // Use the oldest available newsletter for template selection
        const oldestNewsletter = data.length > 0 ? [data[0]] : [];
        setNewsletters(oldestNewsletter);
        
        // Set current selected template from company settings
        const currentTemplate = company?.brandingSettings?.defaultTemplate;
        if (currentTemplate) {
          setSelectedTemplate(currentTemplate);
          setShouldScrollToSelected(true);
        }
      } catch (error) {
        console.error('Error fetching newsletters:', error);
        showToast('error', 'Erreur lors du chargement des templates');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNewslettersAndTemplates();
  }, [jwt, company]);

  // Scroll to selected template when templates are loaded
  useEffect(() => {
    if (shouldScrollToSelected && selectedTemplate && newsletters.length > 0) {
      const selectedTemplateElement = document.getElementById(`template-${selectedTemplate}`);
      if (selectedTemplateElement) {
        selectedTemplateElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        setShouldScrollToSelected(false);
      }
    }
  }, [shouldScrollToSelected, selectedTemplate, newsletters]);

  const handleTemplateSelect = async(templateName: string) => {
    if (isUpdating || !company?.id || !jwt) {return;}

    setIsUpdating(true);
    try {
      // Optimistically reflect selection and notify parent so the wizard can proceed immediately
      setSelectedTemplate(templateName);
      if (onSelected) {
        try {
          onSelected(templateName);
        } catch {
          // no-op
        }
      }

      // Create the proper structure expected by the backend
      const currentBrandingSettings = company.brandingSettings || {};
      const updatedBrandingSettings = {
        ...currentBrandingSettings,
        defaultTemplate: templateName,
      };

      const response = await fetch(
        `/api/companies/${company.id}/branding-settings`,
        {
          ...JWTHeader(jwt),
          method: 'PATCH',
          body: JSON.stringify({
            brandingSettings: updatedBrandingSettings,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to update template preference');
      }

      const updatedCompany = await response.json();
      
      // Update Redux store
      dispatch(updateCompanyBranding(updatedCompany.brandingSettings));

      showToast('success', `Template "${templateName}" sélectionné avec succès`);
    } catch (error) {
      console.error('Error updating template:', error);
      showToast('error', 'Erreur lors de la sélection du template');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="animate-pulse">
          <div className="mb-4 h-8 w-1/3 rounded bg-gray-200"></div>
          <div className="mb-6 h-4 w-2/3 rounded bg-gray-200"></div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {[...Array(2)].map((_, index) => (
              <div key={index} className="rounded-lg border p-4">
                <div className="mb-4 h-48 rounded bg-gray-200"></div>
                <div className="mb-2 h-4 rounded bg-gray-200"></div>
                <div className="h-8 rounded bg-gray-200"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-md">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center">
          {!hideBackButton && (
            <button
              onClick={handleBack}
              className="mr-4 text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Sélection du template de newsletter
            </h1>
            <p className="mt-2 text-gray-600">
              Choisissez le template qui sera utilisé par défaut pour vos newsletters à partir des templates de la plus ancienne newsletter disponible.
              Vous pourrez toujours changer de template lors de la création d'une newsletter.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {newsletters.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-gray-600">Aucune newsletter disponible</p>
            <p className="mt-2 text-sm text-gray-500">
              Aucune newsletter fixe n'est disponible pour la sélection de templates.
            </p>
          </div>
        ) : (
          newsletters.map((newsletter) => (
            <div key={`${newsletter.date}-${newsletter.slug}`} className="space-y-4">
              <div className="border-b border-gray-200 pb-2">
                <h2 className="text-xl font-semibold text-gray-800">Exemple de newsletter : {newsletter.title}</h2>
                <p className="text-sm text-gray-500">
                  {new Date(newsletter.date).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
              
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {newsletter.templates.map((template) => (
                  <div
                    key={`${newsletter.date}-${newsletter.slug}-${template.name}`}
                    id={`template-${template.name}`}
                    className={`rounded-lg border p-4 transition-all ${
                      selectedTemplate === template.name
                        ? 'border-gray-200 ring-2 ring-green-500'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="relative">
                      {selectedTemplate === template.name && (
                        <div className="absolute top-2 right-2 z-10 rounded-full bg-green-500 p-1 text-white">
                          <CheckIcon className="h-4 w-4" />
                        </div>
                      )}
                        
                      {template.pageCount > 0 ? (
                        <div className="relative mb-4 aspect-[3/4]">
                          <PreviewViewer
                            date={newsletter.date}
                            slug={newsletter.slug}
                            pageCount={template.pageCount}
                            templateName={template.name}
                            className="h-full w-full"
                            showNavigation={true}
                          />
                        </div>
                      ) : (
                        <div className="mb-4 flex aspect-[3/4] items-center justify-center rounded bg-gray-100">
                          <span className="px-4 text-center text-gray-500">
                            Template<br/><strong>{template.name}</strong>
                          </span>
                        </div>
                      )}
                        
                      <h3 className="mb-2 text-center text-lg font-semibold">{template.name}</h3>
                      
                      <div className="space-y-2">
                        {selectedTemplate !== template.name && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTemplateSelect(template.name);
                            }}
                            disabled={isUpdating}
                            className="w-full rounded bg-green-500 px-4 py-2 text-sm text-white transition-colors hover:bg-green-600 disabled:opacity-50"
                          >
                            {isUpdating ? 'Mise à jour...' : 'Choisir ce template'}
                          </button>
                        )}
                          
                        {selectedTemplate === template.name && (
                          <div className="w-full rounded bg-green-100 px-4 py-2 text-center text-sm font-medium text-green-800">
                            Template sélectionné
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default NewsletterTemplateSelection; 